import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  FileText,
  Loader2,
  Minus,
  Pencil,
  Plus,
  RotateCcw,
  TriangleAlert,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TankRentalAuditDoc } from '@/components/audit/TankRentalAuditDoc';
import { useAuditDocument } from '@/hooks/useAudit';
import { useChargeMissingCylinders, useDecideCylinderAudit } from '@/hooks/admin/useCylinderAudits';
import { LOST_CYLINDER_FEE, missingTanks } from '@/lib/cylinderProgram';

/**
 * AdminAuditReview — /admin/audits/:auditId, the page the shortage-alert
 * email links to. Renders the full Tank Rental Audit document (counts,
 * photos, both signatures) and, while the audit is pending_approval, an
 * EDITABLE final-counts table + the Approve / Decline decision bar.
 *
 * Edits here don't touch the driver's signed numbers — they become the
 * audit's final_counts (stamped by the approver) and the CR-ADJ invoice is
 * booked from them.
 */

export default function AdminAuditReview() {
  const { auditId } = useParams<{ auditId: string }>();
  const { data: audit, isLoading, error, refetch } = useAuditDocument({ id: auditId ?? null });
  const decide = useDecideCylinderAudit();
  const charge = useChargeMissingCylinders();
  const [chargeTanks, setChargeTanks] = useState<number | null>(null);
  const [chargeNote, setChargeNote] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [outcome, setOutcome] = useState<string | null>(null);
  // final counted per label — seeded from the driver's signed numbers.
  const [finalCounts, setFinalCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!audit) return;
    setFinalCounts((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const seeded: Record<string, number> = {};
      for (const c of audit.counts) seeded[c.label] = c.counted;
      return seeded;
    });
  }, [audit]);

  async function act(action: 'approve' | 'decline') {
    if (!auditId || !audit) return;
    if (
      action === 'decline' &&
      !window.confirm('Decline this audit? No adjustment invoice will be created.')
    ) {
      return;
    }
    const amendments = audit.counts
      .filter((c) => (finalCounts[c.label] ?? c.counted) !== c.counted)
      .map((c) => ({ label: c.label, counted: finalCounts[c.label]! }));
    try {
      const res = await decide.mutateAsync({
        auditId,
        action,
        notes: notes.trim() || undefined,
        counts: action === 'approve' && amendments.length > 0 ? amendments : undefined,
      });
      setOutcome(res.note);
      void refetch();
    } catch {
      // decide.error renders below
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 px-6 py-16 text-brix-muted">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading audit…</span>
      </div>
    );
  }
  if (error || !audit) {
    return (
      <div className="px-6 py-8">
        <div className="rounded-xl border border-brix-border bg-brix-surface p-5 text-sm">
          <div className="font-semibold text-brix-ink">Couldn’t load this audit.</div>
          <p className="mt-1 text-brix-muted">{error?.message ?? 'Audit not found.'}</p>
          <Button asChild variant="secondary" className="mt-4">
            <Link to="/admin/audits">Back to audits</Link>
          </Button>
        </div>
      </div>
    );
  }

  const pending = audit.status === 'pending_approval';
  // The missing-tank charge reads the FINAL counts (the manager's, when
  // amended) — the same numbers the CR-ADJ invoice was booked from.
  const finalEntries = audit.final_counts ?? audit.counts;
  const missing = missingTanks(finalEntries);
  const chargeable = !pending && missing > 0 && (audit.status === 'adjusted' || audit.status === 'invoice_failed');
  const tanksToCharge = Math.min(missing, Math.max(1, chargeTanks ?? missing));
  const chargeAmount = tanksToCharge * LOST_CYLINDER_FEE;
  const money = (n: number) => `$${n.toFixed(2)}`;
  async function chargeMissing() {
    if (!audit) return;
    if (
      !window.confirm(
        `Bill ${tanksToCharge} missing tank${tanksToCharge === 1 ? '' : 's'} at ${money(LOST_CYLINDER_FEE)} each ` +
          `(${money(chargeAmount)}) to this customer in QuickBooks? This creates a real invoice and can only be done once.`,
      )
    )
      return;
    try {
      const r = await charge.mutateAsync({ auditId: audit.id, tanks: tanksToCharge });
      setChargeNote(r.note);
    } catch {
      /* charge.error renders */
    }
  }
  const anyAmended = audit.counts.some((c) => (finalCounts[c.label] ?? c.counted) !== c.counted);

  return (
    <div className="space-y-4 px-6 py-6">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/admin/audits"
          className="inline-flex items-center gap-1 text-sm text-brix-muted transition-colors hover:text-brix-ink"
        >
          <ArrowLeft className="h-4 w-4" /> Audits
        </Link>
        <span className="font-mono text-sm font-bold text-brix-primary">{audit.doc_number}</span>
        {audit.qbo_invoice_id ? (
          <Button asChild size="sm" variant="ghost">
            <Link to={`/invoices/${audit.qbo_invoice_id}`}>
              <FileText size={13} /> View invoice
            </Link>
          </Button>
        ) : null}
      </div>

      {audit.status === 'invoice_failed' ? (
        <div className="rounded-xl border border-brix-warning/40 bg-brix-warning/10 p-4 text-sm text-brix-ink">
          <div className="flex items-start gap-2">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-brix-warning" />
            <div>
              <span className="font-semibold">The QuickBooks invoice did not book.</span>{' '}
              {audit.invoice_error ? (
                <>
                  QuickBooks said: <span className="font-mono text-xs">{audit.invoice_error}</span>
                </>
              ) : (
                'No reason was recorded (this audit predates error recording).'
              )}{' '}
              The count is on file; use <span className="font-semibold">Retry invoice</span> on the
              Audits list to book it. A number QuickBooks already used is renumbered automatically.
            </div>
          </div>
        </div>
      ) : null}

      {/* Decision panel with editable final counts */}
      {pending ? (
        <div className="rounded-xl border border-brix-warning/40 bg-brix-warning/10 p-4">
          <div className="flex items-start gap-2">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-brix-warning" />
            <div className="text-sm text-brix-ink">
              <span className="font-semibold">Shortage — approval required.</span> The driver
              counted fewer tanks than the records. You can adjust the final numbers below before
              deciding — the driver’s signed counts stay on the record, and the $0{' '}
              <span className="font-mono">{audit.doc_number}</span> invoice is booked from your
              final numbers.
            </div>
          </div>

          {/* Editable final counts */}
          <div className="mt-3 overflow-hidden rounded-lg border border-brix-border bg-brix-surface">
            <div className="grid grid-cols-[1fr_70px_80px_150px_70px] items-center gap-2 border-b border-brix-border bg-brix-surface-alt/60 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-brix-muted">
              <div>Cylinder</div>
              <div className="text-right">Records</div>
              <div className="text-right">Driver</div>
              <div className="text-center">Final count</div>
              <div className="text-right">Delta</div>
            </div>
            {audit.counts.map((c) => {
              const value = finalCounts[c.label] ?? c.counted;
              const delta = value - c.system_count;
              const edited = value !== c.counted;
              return (
                <div
                  key={c.label}
                  className="grid grid-cols-[1fr_70px_80px_150px_70px] items-center gap-2 border-b border-brix-border px-3 py-2 text-sm last:border-b-0"
                >
                  <div className="flex items-center gap-1.5 font-semibold text-brix-ink">
                    {c.label}
                    {edited ? <Pencil size={12} className="text-brix-primary" /> : null}
                  </div>
                  <div className="text-right font-mono text-brix-muted">{c.system_count}</div>
                  <div className="text-right font-mono text-brix-muted">{c.counted}</div>
                  <div className="flex items-center justify-center gap-1">
                    <button
                      type="button"
                      aria-label={`Fewer ${c.label}`}
                      onClick={() =>
                        setFinalCounts((p) => ({ ...p, [c.label]: Math.max(0, value - 1) }))
                      }
                      className="flex h-7 w-7 items-center justify-center rounded-md border border-brix-border text-brix-ink transition-colors hover:bg-brix-primary-tint"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span
                      className={`w-9 text-center font-mono font-bold ${
                        edited ? 'text-brix-primary' : 'text-brix-ink'
                      }`}
                    >
                      {value}
                    </span>
                    <button
                      type="button"
                      aria-label={`More ${c.label}`}
                      onClick={() =>
                        setFinalCounts((p) => ({ ...p, [c.label]: Math.min(999, value + 1) }))
                      }
                      className="flex h-7 w-7 items-center justify-center rounded-md border border-brix-border text-brix-ink transition-colors hover:bg-brix-primary-tint"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                    {edited ? (
                      <button
                        type="button"
                        aria-label={`Reset ${c.label} to driver count`}
                        title="Reset to driver count"
                        onClick={() => setFinalCounts((p) => ({ ...p, [c.label]: c.counted }))}
                        className="ml-0.5 flex h-7 w-7 items-center justify-center rounded-md text-brix-muted transition-colors hover:bg-brix-primary-tint hover:text-brix-ink"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>
                  <div
                    className={`text-right font-mono font-bold ${
                      delta === 0
                        ? 'text-brix-muted'
                        : delta > 0
                          ? 'text-brix-success'
                          : 'text-brix-warning'
                    }`}
                  >
                    {delta === 0 ? '—' : `${delta > 0 ? '+' : ''}${delta}`}
                  </div>
                </div>
              );
            })}
          </div>
          {anyAmended ? (
            <p className="mt-2 text-xs text-brix-muted">
              Amended numbers are recorded as your approval decision — the driver’s signed counts
              stay on the audit unchanged.
            </p>
          ) : null}

          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Decision notes (optional)"
              className="sm:max-w-sm"
            />
            <div className="flex gap-2">
              <Button disabled={decide.isPending} onClick={() => void act('approve')}>
                {decide.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Check size={14} />
                )}
                {anyAmended ? 'Approve with changes' : 'Approve & book invoice'}
              </Button>
              <Button
                variant="secondary"
                disabled={decide.isPending}
                onClick={() => void act('decline')}
              >
                <X size={14} /> Decline
              </Button>
            </div>
          </div>
          {decide.error ? (
            <div className="mt-2 text-xs text-brix-warning">{decide.error.message}</div>
          ) : null}
        </div>
      ) : outcome ? (
        <div className="rounded-xl border border-brix-border bg-brix-surface px-4 py-3 text-sm text-brix-ink">
          {outcome}
        </div>
      ) : null}

      {/* Missing-tank replacement charge — the MONEY half of a shortage. The
          CR-ADJ invoice corrected the count; this bills the tanks that are
          gone, once, at the program rate. */}
      {audit.charge_qbo_invoice_id ? (
        <div className="rounded-xl border border-brix-border bg-brix-surface px-4 py-3 text-sm text-brix-ink">
          <span className="font-semibold">Missing tanks charged.</span>{' '}
          {audit.charge_tanks} tank{audit.charge_tanks === 1 ? '' : 's'} × {money(Number(audit.charge_rate ?? LOST_CYLINDER_FEE))} ={' '}
          <span className="font-mono">{money(Number(audit.charge_amount ?? 0))}</span> on{' '}
          <span className="font-mono">{audit.charge_doc_number}</span>
          {audit.charged_at ? ` · ${new Date(audit.charged_at).toLocaleDateString()}` : ''}
          {audit.charged_by_email ? ` · by ${audit.charged_by_email}` : ''}.{' '}
          <Link className="text-brix-primary underline" to={`/invoices/${audit.charge_qbo_invoice_id}`}>
            View invoice
          </Link>
        </div>
      ) : chargeable ? (
        <div className="rounded-xl border border-brix-border bg-brix-surface p-4">
          <div className="flex items-start gap-2">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-brix-warning" />
            <div className="text-sm text-brix-ink">
              <span className="font-semibold">
                {missing} tank{missing === 1 ? '' : 's'} missing.
              </span>{' '}
              The count has been corrected; the tanks themselves have not been paid for. The
              program rate is {money(LOST_CYLINDER_FEE)} per missing cylinder. Charge fewer only if
              some were found — never more than the audit shows.
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-brix-ink">
              Tanks to charge
              <Input
                type="number"
                min={1}
                max={missing}
                value={tanksToCharge}
                onChange={(e) => setChargeTanks(Number(e.target.value))}
                className="w-20"
              />
            </label>
            <Button disabled={charge.isPending} onClick={() => void chargeMissing()}>
              {charge.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Charge {money(chargeAmount)} for missing tanks
            </Button>
          </div>
          {charge.error ? <div className="mt-2 text-xs text-brix-warning">{charge.error.message}</div> : null}
          {chargeNote ? <div className="mt-2 text-xs text-brix-muted">{chargeNote}</div> : null}
        </div>
      ) : null}

      {/* The full merged audit document. */}
      <div className="rounded-xl border border-brix-border bg-brix-surface p-5 shadow-card">
        <TankRentalAuditDoc audit={audit} />
      </div>
    </div>
  );
}
